import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth, hasRole } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

const ROLES = ["viewer", "moderator", "org_admin", "org_owner"];
const roleLabel = { org_owner: "Owner", org_admin: "Admin", moderator: "Moderator", viewer: "Viewer" };

export default function Team() {
  const { role, user, activeMembership } = useAuth();
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [saving, setSaving] = useState(false);
  const canManage = hasRole(role, "org_admin");

  const load = () => api.get("/org/members").then(({ data }) => setMembers(data)).catch(() => setMembers([]));
  useEffect(() => { load(); }, [activeMembership?.org_id]);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/org/members", form);
      toast.success("Member added");
      setForm({ name: "", email: "", password: "", role: "viewer" }); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const changeRole = async (uid, r) => { try { await api.put(`/org/members/${uid}/role`, { role: r }); toast.success("Role updated"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const remove = async (uid) => { try { await api.delete(`/org/members/${uid}`); toast.success("Member removed"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div className="p-5 sm:p-8 max-w-4xl">
      <PageHeader title="Team" subtitle="Manage who has access to this organisation."
        action={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-md bg-accent text-accent-foreground hover:opacity-90 transition-opacity" data-testid="invite-member-btn"><Plus className="h-4 w-4 mr-1.5" /> Add member</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add a team member</DialogTitle></DialogHeader>
              <form onSubmit={invite} className="space-y-4" data-testid="invite-form">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="invite-name" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="invite-email" /></div>
                <div><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="mt-1.5" data-testid="invite-password" placeholder="Min. 8 characters" /></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.filter((r) => r !== "org_owner" || role === "org_owner").map((r) => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <DialogFooter><Button type="submit" disabled={saving} className="rounded-md" data-testid="invite-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add member"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )} />

      {!members ? <Skeleton className="h-64 rounded-xl" /> : members.length === 0 ? (
        <EmptyState icon={Shield} title="No members" description="Invite teammates to collaborate." />
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="members-list">
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between p-4 gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">{m.name?.[0]?.toUpperCase() || "?"}</span>
                <div className="min-w-0"><p className="font-medium text-foreground truncate">{m.name} {m.user_id === user?.id && <span className="text-xs text-muted-foreground">(you)</span>}</p><p className="text-xs text-muted-foreground truncate">{m.email}</p></div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {canManage && m.user_id !== user?.id ? (
                  <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`role-${m.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.filter((r) => r !== "org_owner" || role === "org_owner").map((r) => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{roleLabel[m.role]}</span>
                )}
                {canManage && m.user_id !== user?.id && (
                  <button onClick={() => remove(m.user_id)} className="text-muted-foreground hover:text-destructive p-1.5" data-testid={`remove-${m.user_id}`}><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
